import React, { useState, useMemo } from 'react';
import { ChartProps } from './types';

type SortDirection = 'asc' | 'desc';
type SortColumn = 'dimension' | 'measure';

const PAGE_SIZES = [10, 25, 50, 100] as const;

/**
 * Sortable, paginated data table for analytics results.
 * Default sort: first column (dimension) ascending.
 * Default page size: 10 rows.
 */
export function DataTable({ data, dimensionLabel, measureLabel, onSegmentClick }: ChartProps): React.ReactElement {
  const [sortColumn, setSortColumn] = useState<SortColumn>('dimension');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      if (sortColumn === 'dimension') {
        const cmp = a.dimensionValue.localeCompare(b.dimensionValue);
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      const cmp = a.measureValue - b.measureValue;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [data, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const getSortIndicator = (column: SortColumn): string => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">
        No data available
      </div>
    );
  }

  return (
    <div className="w-full" role="region" aria-label={`Data table: ${dimensionLabel} by ${measureLabel}`}>
      <div className="relative w-full overflow-x-auto">
        <table className="w-full caption-bottom text-sm">
          <thead className="[&_tr]:border-b">
            <tr className="border-b">
              <th
                scope="col"
                className="h-12 cursor-pointer select-none px-4 text-left align-middle font-medium text-muted-foreground hover:text-foreground"
                onClick={() => handleSort('dimension')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('dimension');
                  }
                }}
                tabIndex={0}
                aria-sort={sortColumn === 'dimension' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                role="columnheader"
              >
                {dimensionLabel}{getSortIndicator('dimension')}
              </th>
              <th
                scope="col"
                className="h-12 cursor-pointer select-none px-4 text-right align-middle font-medium text-muted-foreground hover:text-foreground"
                onClick={() => handleSort('measure')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('measure');
                  }
                }}
                tabIndex={0}
                aria-sort={sortColumn === 'measure' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                role="columnheader"
              >
                {measureLabel}{getSortIndicator('measure')}
              </th>
            </tr>
          </thead>
          <tbody className="[&_tr:last-child]:border-0">
            {paginatedData.map((row) => (
              <tr
                key={row.dimensionValue}
                className="cursor-pointer border-b transition-colors hover:bg-muted/50"
                onClick={() => onSegmentClick?.(row.dimensionValue)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSegmentClick?.(row.dimensionValue);
                  }
                }}
                tabIndex={0}
                role="row"
                aria-label={`${dimensionLabel}: ${row.dimensionValue}, ${measureLabel}: ${row.measureValue}`}
              >
                <td className="p-4 align-middle">{row.dimensionValue}</td>
                <td className="p-4 text-right align-middle">{row.measureValue.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between border-t px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <label htmlFor="page-size-select">Rows per page:</label>
          <select
            id="page-size-select"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="rounded border border-input bg-background px-2 py-1 text-sm"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="rounded border border-input px-3 py-1 text-sm disabled:opacity-50"
            aria-label="Previous page"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="rounded border border-input px-3 py-1 text-sm disabled:opacity-50"
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
